type EasingOptions = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';

export interface IScrollToCustomOptions extends ScrollToOptions {
    duration?: number;
    easing?: EasingOptions;
    offset?: number;
}

export async function scrollTo (
    el: Element | Window,
    options?: IScrollToCustomOptions
) {
    if (!(el instanceof Element) && !(el instanceof Window)) {
        throw new Error(
            `element passed to scrollTo() must be either the window or a DOM element, you passed ${el}!`
        );
    }

    options = sanitizeScrollOptions(options);

    const scroll = (
        from: number,
        to: number,
        prop: string,
        startTime: number,
        duration: number | undefined = 500,
        easeFunc: (time: number) => number,
        callback: () => void
    ) => {
        window.requestAnimationFrame(() => {
            const currentTime = Date.now();
            const time = Math.min(1, (currentTime - startTime) / duration);

            if (from === to) {
                return callback ? callback() : null;
            }

            const pos2 = Math.ceil(easeFunc(time) * (to - from) + from);

            /* prevent scrolling, if already there, or at end, or if user scrolls during transition */

            if (from === getScrollPosition(el)) {
                setScrollPosition(el, pos2);
                if (time < 1) {
                    scroll(
                        getScrollPosition(el),
                        to,
                        prop,
                        startTime,
                        duration,
                        easeFunc,
                        callback
                    );
                } else if (callback) {
                    callback();
                }
            } else if (callback) {
                callback();
            }
        });
    };

    const currentScrollPosition = getScrollPosition(el);
    const scrollProperty = getScrollPropertyByElement(el);
    return new Promise((resolve) => {
        scroll(
            currentScrollPosition,
            options && options.top ? options.top : 0,
            scrollProperty,
            Date.now(),
            options && options.duration ? options.duration : 500,
            getEasing(options && options.easing ? options.easing : 'ease-in-out'),
            resolve
        );
    });
}

export function scrollIntoView (
    element: HTMLElement,
    scroller?: Element,
    options?: IScrollToCustomOptions
) {
    validateElement(element);
    if (scroller && !(scroller instanceof Element)) {
        options = scroller;
        scroller = undefined;
    }
    const { duration, easing } = sanitizeScrollOptions(options);
    scroller = scroller || utils.getDocument().body;
    let currentContainerScrollYPos = 0;
    let elementScrollYPos = element ? element.offsetTop : 0;
    const document = utils.getDocument();

    // if the container is the document body or document itself, we'll
    // need a different set of coordinates for accuracy
    if (scroller === document.body || scroller === document.documentElement) {
        // using pageYOffset for cross-browser compatibility
        currentContainerScrollYPos = window.pageYOffset;
        // must add containers scroll y position to ensure an absolute value that does not change
        elementScrollYPos =
            element.getBoundingClientRect().top + currentContainerScrollYPos;
    }

    return scrollTo(scroller, {
        top: elementScrollYPos - (options?.offset || 0),
        left: 0,
        duration,
        easing,
    });
}

function validateElement (element?: HTMLElement) {
    if (element === undefined) {
        const errorMsg =
            'The element passed to scrollIntoView() was undefined.';
        throw new Error(errorMsg);
    }
    if (!(element instanceof HTMLElement)) {
        throw new Error(
            `The element passed to scrollIntoView() must be a valid element. You passed ${element}.`
        );
    }
}

function getScrollPropertyByElement (el: Element | Window) {
    const props = {
        window: {
            y: 'pageYOffset',
            x: 'pageXOffset',
        },
        element: {
            y: 'scrollTop',
            x: 'scrollLeft',
        },
    };
    const axis = 'y';
    const document = utils.getDocument();
    if (el === document.body) {
        return props.element[axis];
    } else if (el instanceof Window) {
        return props.window[axis];
    } else {
        return props.element[axis];
    }
}

function sanitizeScrollOptions (
    options?: IScrollToCustomOptions
): IScrollToCustomOptions {
    if (!options) {
        return {};
    }
    if (options.behavior === 'smooth') {
        options.easing = 'ease-in-out';
        options.duration = options.duration ? options.duration : 500;
    }
    if (options.behavior === 'auto') {
        options.duration = options.duration ? options.duration : 500;
        options.easing = 'linear';
    }
    return options;
}

function getScrollPosition (el: Element | Window): number {
    const document = utils.getDocument();
    const prop = getScrollPropertyByElement(el);
    if (el === document.body || el === document.documentElement) {
        return document.body[prop] || (document.documentElement && document.documentElement[prop]);
    } else if (el instanceof Window) {
        return window[prop];
    } else {
        return el[prop];
    }
}

function setScrollPosition (el: Element | Window, value: number) {
    if (el instanceof Window) {
        el.scrollTo(0, value);
    } else {
        el.scrollTop = value;
    }
}

export const utils = {
    getDocument (): HTMLDocument {
        return document;
    },
};

type EasingFunction = (t: number) => number;

interface IEasingFunctions {
    linear: EasingFunction;
    'ease-in': EasingFunction;
    'ease-out': EasingFunction;
    'ease-in-out': EasingFunction;
}

export const easingMap: IEasingFunctions = {
    'linear' (t: number) {
        return t;
    },
    'ease-in' (t: number) {
        return t * t;
    },
    'ease-out' (t: number) {
        return t * (2 - t);
    },
    'ease-in-out' (t: number) {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    },
};

const getEasing = (easing: EasingOptions): EasingFunction => {
    const defaultEasing = 'linear';
    const easeFunc = easingMap[easing || defaultEasing];
    if (!easeFunc) {
        const options = Object.keys(easingMap).join(',');
        throw new Error(
            `Scroll error: scroller does not support an easing option of "${easing}". Supported options are ${options}`
        );
    }
    return easeFunc;
};
